import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader } from 'react-bootstrap';
import { registerUserForOtp, sendOtp, verifyOtp, registerUser } from '../../apis/mobileNumber';
import message from '../../assets/message.svg';
import editIcon from '../../assets/edit.svg';
import verifiedIcon from '../../assets/tick-circle.svg';
import FaceRegistration from '../FaceRegistration/FaceRegistration';
import camera from '../../assets/camera.svg';
import { checkAgeGreaterThanEighteen, checkIfOnlyText } from '../../helpers';

let timerInterval = null;

function SignupPage({
    showSignupModal, setShowSignupModal, setShowSuccessModal, setCandidateId, setResponceMessage
}) {
    const inputRef = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
    const formRef = useRef();
    const [userData, setUserData] = useState({
        "firstName": "",
        "lastName": "",
        "mobileNumber": '',
        "dob": "",
        "email": "",
        "gender": "",
        "education": "",
        "industry": "",
        "experience": 'fresher',
        "address": "",
        "pincode": '',
        "userPhoto": "",
        "source": ""
    });
    const [otpSent, setOtpSent] = useState(false);
    const [verifyingOtp, setVerifyingOtp] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [enteredOtp, setEnteredOtp] = useState(['', '', '', '', '', '']);
    const [resendTimer, setResendTimer] = useState(180);
    const [startTimer, setStartTimer] = useState(false);
    const [showFaceRegistrationModal, setShowFaceRegistrationModal] = useState(false);
    const [errorState, setErrorState] = useState([]);
    const [submitErrorMessage, setSubmitErrorMessage] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [otpError, setOtpError] = useState(null);
    const [mobileVerifyError, setMobileVerifyError] = useState(null);

    useEffect(() => {
        if (startTimer && resendTimer > 0) {
            timerInterval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, [1000])
        }

        if (resendTimer === 0) {
            clearInterval(timerInterval);
            timerInterval = null;
        }

        return () => {
            if (timerInterval) {
                clearInterval(timerInterval);
                timerInterval = null;
            }
        }
    }, [startTimer, resendTimer]);

    useEffect(() => {
        // Function to parse URL parameters
        const getUrlParam = (param) => {
            const params = new URLSearchParams(window?.location?.search);
            return params.get(param);
        };

        // Read 'source' parameter from URL and set it in state
        const sourceParam = getUrlParam('source');
        if (sourceParam) {
            setUserData((prevUserData) => ({
                ...prevUserData,
                source: sourceParam
            }));
        }
    }, []);

    const handleMobileVerify = (isResend, callback) => {
        if (isResend) {
            setVerifyingOtp(true);
            sendOtp(userData.mobileNumber)
                .then((res) => {
                    if (res?.data?.success) {
                        setOtpSent(true);
                        setStartTimer(true);
                        callback?.();
                    }
                })
                .catch((err) => {
                    console.log('err', err);
                    setMobileVerifyError(err?.response?.data?.message || 'Failed to send otp')
                })
                .finally(() => {
                    setVerifyingOtp(false);
                })
        } else {
            setVerifyingOtp(true);
            registerUserForOtp(userData?.firstName, userData?.lastName, userData.mobileNumber)
                .then((res) => {
                    if (res?.data?.success) {
                        setOtpSent(true);
                        setStartTimer(true);
                        callback?.();
                    }
                })
                .catch((err) => {
                    console.log('err', err);
                    setMobileVerifyError(err?.response?.data?.message || 'Failed to send otp')
                })
                .finally(() => {
                    setVerifyingOtp(false);
                })
        }
    }

    const handleResendOtp = () => {
        setMobileVerifyError(null);
        setOtpError(null);
        if (timerInterval) {
            clearInterval(timerInterval);
            timerInterval = null;
        }
        setResendTimer(180);
        setStartTimer(false);
        handleMobileVerify(true, () => {
            setStartTimer(true);
        });
    }

    const handleOtpChange = (index, value) => {
        if (value.toString()?.length < 2) {
            setOtpError(null);
            const tempVal = [...enteredOtp];
            tempVal[parseInt(index)] = value;
            setEnteredOtp([...tempVal]);
            if (index < 5 && value) {
                inputRef[index + 1].current.focus();
            }
            // if (index > 0 && !value) {
            //     inputRef[index-1].current.focus();
            // }
        }
    }

    const handleKeyChange = (index, e) => {
        if (e.keyCode === 8) { // backspace
            if (index > 0 && !inputRef[index].current.value) {
                inputRef[index - 1].current.focus();
            }
        }
    }

    const handleOtpSubmit = () => {
        setVerifyingOtp(true);
        setMobileVerifyError(null);
        verifyOtp(userData?.mobileNumber, enteredOtp?.join(''))
            .then((res) => {
                setOtpVerified(true);
                setOtpSent(false);
            })
            .catch((err) => {
                setOtpError(err?.response?.data?.message || 'OTP verification failed')
            })
            .finally(() => {
                setVerifyingOtp(false);
            })
    }

    const handleDataUpdate = (key, value) => {
        setMobileVerifyError(null);
        if (key === 'firstName') {
            if (checkIfOnlyText(value)) {
                setUserData((prevData) => ({
                    ...prevData,
                    [key]: value
                }));
            }
            return;
        }
        if (key === 'lastName') {
            if (checkIfOnlyText(value)) {
                setUserData((prevData) => ({
                    ...prevData,
                    [key]: value
                }));
            }
            return;
        }
        if (key === 'experience' && (value === 'fresher' || value === '<1 year')) {
            setUserData((prevData) => ({
                ...prevData,
                [key]: value,
                industry: ''
            }));
            return;
        }
        if (key === 'mobileNumber') {
            setOtpVerified(false);
            setOtpSent(false);
        }
        setSubmitErrorMessage(null);
        if (errorState?.length > 0) {
            setErrorState([]);
        }
        if (key === 'pincode') {
            const regex = /^[0-9]*$/;
            if (regex.test(value) || value === '' || value.length <= 6) {
                setUserData((prevData) => ({
                    ...prevData,
                    [key]: Number(value.slice(0, 6).replace(/\D/g, ''))
                }));
            }
        } else {
            setUserData((prevData) => ({
                ...prevData,
                [key]: value
            }));
        }
    };

    useEffect(() => {
        if (otpVerified) {
            setErrorState([]);
        }
    }, [otpVerified]);

    const handleSubmit = () => {
        setRegistering(true);
        setSubmitErrorMessage(null);
        let hasError = false;
        if (
            !otpVerified ||
            !userData?.mobileNumber) {
            setErrorState((prev) => [...prev, 'mobile']);
            hasError = true;
        }
        if (!userData.firstName) {
            setErrorState((prev) => [...prev, 'firstName']);
            hasError = true;
        }
        if (!userData.lastName) {
            setErrorState((prev) => [...prev, 'lastName']);
            hasError = true;
        }
        if (!userData.experience) {
            setErrorState((prev) => [...prev, 'experience']);
            hasError = true;
        }
        if (!userData.education) {
            setErrorState((prev) => [...prev, 'education']);
            hasError = true;
        }
        if (!userData.dob) {
            setErrorState((prev) => [...prev, 'dob']);
            hasError = true;
        }
        if (userData.dob && !checkAgeGreaterThanEighteen(userData.dob)) {
            setErrorState((prev) => [...prev, 'wrongDob']);
            hasError = true;
        }
        if (!userData.gender) {
            setErrorState((prev) => [...prev, 'gender']);
            hasError = true;
        }
        if ((userData?.experience !== 'fresher' && userData?.experience !== '<1 year') && !userData.industry) {
            setErrorState((prev) => [...prev, 'industry']);
            hasError = true;
        }
        if (!userData.address) {
            setErrorState((prev) => [...prev, 'address']);
            hasError = true;
        }
        if (!userData.pincode || userData.pincode?.toString()?.length !== 6) {
            setErrorState((prev) => [...prev, 'pincode']);
            hasError = true;
        }
        if(!userData.email) {
            setErrorState((prev) => [...prev, 'email']);
            hasError = true;
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(userData.email)) {
                setErrorState((prev) => [...prev, 'email']);
                hasError = true;
            } else if (userData.email.length > 30) {
                setErrorState((prev) => [...prev, 'emailLength']);
                hasError = true;
            }
        }
        if (!userData.userPhoto) {
            setErrorState((prev) => [...prev, 'userPhoto']);
            hasError = true;
        }
        if (hasError) {
            setRegistering(false);
            formRef.current.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
            return;
        } else {
            setRegistering(true);
            const payload = { ...userData };
            payload.mobileNumber = parseInt(userData.mobileNumber);
            registerUser(userData)
                .then((res) => {
                    setRegistering(false);
                    setShowSignupModal(false);
                    setShowSuccessModal(true);
                    if (res.statusCode === 200 && res.hotValidationType === 'S') {
                        setCandidateId(res?.candidateId);
                        setResponceMessage(res?.message);
                    }
                })
                .catch((err) => {
                    setRegistering(false);
                    if (err?.response?.status !== 200 || err?.response?.data?.statusCode !== 200) {
                        setSubmitErrorMessage(err?.response?.data?.error || 'Registration failed as no response from platform.')
                    } else {
                        setSubmitErrorMessage('Registration failed as no response from platform.');
                    }
                })
        }
    }

    const getTimerToDisplay = () => {
        const minutes = parseInt(resendTimer / 60);
        const seconds = parseInt(resendTimer - (minutes * 60));
        return `(0${minutes}:${(seconds < 10) ? `0${seconds}` : seconds})`
    }
  return (
    <Modal
        size='lg'
        show={showSignupModal}
        onHide={() => setShowSignupModal(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        fullscreen={true}
    >
        <ModalHeader>
            <h3 className='titleSignup'>Sign up form</h3>
            <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={() => setShowSignupModal(false)}>
                <span aria-hidden="true">&times;</span>
            </button>
        </ModalHeader>
        <div className="d-flex justify-content-center">
            <div className="col-md-12">
                <div>
                <div className="card-body overflow-auto" ref={formRef} style={{height: '75vh'}}>
                    {/* <form> */}
                    <div className="form-group required">
                        <label>First Name<span className="text-danger"> *</span></label>
                        <input type="text" value={userData.firstName || ''} onChange={(e) => handleDataUpdate('firstName', e.target.value)} className="form-control" name="firstName" id="inputFirstName" placeholder="Enter first name" required />
                        {errorState?.includes('firstName') && <p className='errorMessage'>First name is required</p>}
                    </div>
                    <div className="form-group required">
                        <label>Last Name<span className="text-danger"> *</span></label>
                        <input type="text" value={userData.lastName || ''} onChange={(e) => handleDataUpdate('lastName', e.target.value)} className="form-control" name="lastName" id="inputLastName" placeholder="Enter last name" required />
                        {errorState?.includes('lastName') && <p className='errorMessage'>Last name is required</p>}
                    </div>
                    <div className="form-group required">
                        <label>E-Mail ID<span className="text-danger"> *</span></label>
                        <input type="text" value={userData.email || ''} onChange={(e) => handleDataUpdate('email', e.target.value)} className="form-control" name="email" id="inputEmail" placeholder="someone@example.com" required />
                        {errorState?.includes('email') && <p className='errorMessage'>Please Enter valid email id</p>}
                        {errorState?.includes('emailLength') && <p className='errorMessage'>The maximum length of email is 30 characters.</p>}
                    </div>
                    <div className="form-group required input-group-verify">
                        <label>Mobile Number
                            <span className="text-danger"> *</span>
                        </label>
                        <input type="text" value={userData.mobileNumber || ''} onChange={(e) => handleDataUpdate('mobileNumber', Number(e.target.value))} className="form-control" name="mobileNumber" id="mobileNumber" pattern="[789][0-9]{9}" placeholder="Enter your Mobile Number" required maxLength={10} />
                        {
                            userData?.mobileNumber?.toString()?.length === 10 && (
                                <span className='verify-link'>
                                    {
                                        otpVerified ?
                                        <img src={verifiedIcon} alt='verified' /> :
                                        <span onClick={() => handleMobileVerify()}>Verify</span>
                                    }
                                </span>
                            )
                        }
                        {errorState?.includes('mobile') && <p className='errorMessage'>Mobile number verification is required</p>}
                        {mobileVerifyError && <p className='errorMessage'>{mobileVerifyError}</p>}
                    </div>
                    <div className="form-group required">
                        <div className="form-group">
                        <label>Date of Birth<span className="text-danger"> *</span></label>
                            <input
                                type="date"
                                value={userData.dob || ''}
                                onChange={(e) => handleDataUpdate('dob', e.target.value)}
                                className="form-control datepicker"
                                placeholder="Enter your date of birth"
                                id="dob"
                                name="dob"
                                min="1960-01-01"
                                required
                                onKeyDown={(e) => e.preventDefault()}
                                />
                            {errorState?.includes('dob') && <p className='errorMessage'>Date of birth is required</p>}
                            {errorState?.includes('wrongDob') && <p className='errorMessage'>Age should be greater than 18 years</p>}
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Gender<span className="text-danger"> *</span></label>
                        <select value={userData.gender} onChange={(e) => handleDataUpdate('gender', e.target.value)} className="form-control" id="gender" name="gender" required>
                            <option value="" selected disabled>Select Gender</option>
                            <option value="MALE">Male</option>
                            <option value="FEMALE">Female</option>
                            <option value="OTHER">Do not want to share</option>
                        </select>
                        {errorState?.includes('gender') && <p className='errorMessage'>Gender is required</p>}
                    </div>
                    <div className="form-group">
                        <label>Education<span className="text-danger"> *</span></label>
                        <select value={userData.education} onChange={(e) => handleDataUpdate('education', e.target.value)} className="form-control" name="education" required>
                            <option value="" selected disabled>Please Select Education</option>
                            <option value='GRADUATION'>GRADUATION</option>
                            <option value='POST_GRADUATION'>POST GRADUATION</option>
                            <option value='DIPLOMA'>DIPLOMA</option>
                            <option value='10TH PASS'>10TH PASS </option>
                            <option value='12TH PASS'>12TH PASS</option>
                            <option value='8TH PASS'>8TH PASS</option>
                        </select>
                        {errorState?.includes('education') && <p className='errorMessage'>Education is required</p>}
                    </div>
                    <div className="form-group">
                        <label>Experience<span className="text-danger"> *</span></label>
                        <select value={userData.experience} onChange={(e) => handleDataUpdate('experience', e.target.value)} className="form-control" id="experience" name="experience" required>
                            <option value="" selected disabled>Please Select Experience</option>
                            <option value='fresher'>Fresher</option>
                            <option value='<1 year'>&lt; 1 year</option>
                            <option value='1-2 years'>1 to 2 years</option>
                            <option value='2+ years'>2+ years</option>
                        </select>
                        {errorState?.includes('experience') && <p className='errorMessage'>Experience is required</p>}
                    </div>
                    <div className="form-group required">
                        <label>Industry<span className="text-danger"> *</span></label>
                        {/* <input disabled={userData?.experience === '0-1 years'} value={userData.industry} onChange={(e) => handleDataUpdate('industry', e.target.value)} type="text" className="form-control" name="industry" id="industry" required placeholder="Enter the industry you have worked in" /> */}
                        <select disabled={(userData?.experience === 'fresher' || userData?.experience === '<1 year')} defaultValue="" value={userData.industry} onChange={(e) => handleDataUpdate('industry', e.target.value)} className="form-control" id="industry" name="industry" required>
                            <option value="" selected disabled>Select the industry you have worked in</option>
                            <option value='Grocery'>Grocery</option>
                            <option value='Fashion & Lifestyle'>Fashion & Lifestyle</option>
                            <option value='Electronics'>Electronics</option>
                            <option value='Supply Chain'>Supply Chain</option>
                            <option value='Facility Management & housekeeping'>Facility Management & housekeeping</option>
                            <option value='Security'>Security</option>
                            <option value='After sales service'>After sales service</option>
                            <option value='others'>Others</option>
                        </select>
                        {errorState?.includes('industry') && <p className='errorMessage'>Industry is required</p>}
                    </div>
                    <div className="form-group required">
                        <label>Current Address<span className="text-danger"> *</span></label>
                        <input value={userData.address} onChange={(e) => handleDataUpdate('address', e.target.value)} type="text" className="form-control" name="address" id="address" required placeholder="Enter your address" />
                        {errorState?.includes('address') && <p className='errorMessage'>Address is required</p>}
                    </div>
                    <div className="form-group required">
                        <label>Pincode<span className="text-danger"> *</span></label>
                        <input type='number' pattern="\d*" value={userData.pincode || ''} onChange={(e) => handleDataUpdate('pincode', e.target.value)} className="form-control" required placeholder="Enter pincode" name='pincode' id='pincode' />
                        {errorState?.includes('pincode') && <p className='errorMessage'>Pincode is required</p>}
                    </div>
                    {/* <div className="form-group required">
                        <label>Capture face<span className="text-danger"> *</span></label>
                        <input value={userData.userPhoto} onChange={(e) => handleDataUpdate('userPhoto', e.target.value)} type="text" className="form-control custom-input" name="faceURL" id="inputFaceCapture" required placeholder="Capture your face" />
                    </div> */}
                            <div className="form-group required">
                                <label>Capture face<span className="text-danger"> *</span></label>
                                <div className='form-control ' onClick={() => setShowFaceRegistrationModal(true)}>
                                    {
                                        userData.userPhoto ?
                                            <div className='d-flex justify-content-between'>
                                                <span>Face captured successfully</span>
                                                <img src={camera} alt='camera' />
                                            </div> :
                                            <div className='d-flex justify-content-between'>
                                                <span>Capture your face</span>
                                                <img src={camera} alt='camera' />
                                            </div>
                                    }

                                </div>
                                {errorState?.includes('userPhoto') && <p className='errorMessage'>Face capture is required</p>}
                            </div>
                            {submitErrorMessage && <p className='errorMessage' style={{ textAlign: 'center' }}>{submitErrorMessage}</p>}
                            {/* <div className="sign-up-cta text-center mt-5">
                    {
                        registering
                        ? <button disabled={true} type="submit" id="registrationCTA" className="btn btn-dark">In process...</button>
                        : <button onClick={handleSubmit} type="submit" id="registrationCTA" className="btn btn-dark">Submit</button>
                    }
                    </div> */}
                            {/* </form> */}
                        </div>
                        <div className="sign-up-cta text-center mt-2 mb-2">
                            {
                                registering
                                    ? <button disabled={true} type="submit" id="registrationCTA" className="btn btn-dark">In process...</button>
                                    : <button onClick={handleSubmit} type="submit" id="registrationCTA" className="btn btn-dark">Submit</button>
                            }
                        </div>
                    </div>
                </div>
            </div>
            {
                (otpSent) && (
                    <Modal
                        size='md'
                        show={otpSent}
                        onHide={() => {
                            setOtpSent(false);
                            setVerifyingOtp(false);
                            if (timerInterval) {
                                clearInterval(timerInterval);
                                timerInterval = null;
                            }
                            setStartTimer(false);
                            timerInterval = null
                        }}
                        aria-labelledby="contained-modal-title-vcenter"
                        centered
                        fullscreen={true}
                    >
                        <div className="d-flex justify-content-center mt-3">
                            <div className="col-md-12">
                                <div className="card rounded-0 shadow">
                                    <div className="card-body">
                                        <div className="optContainer mt-5">
                                            <h2 className="text-left">Mobile number verification</h2>
                                            <div className="d-flex justify-content-center m-5">
                                                <img src={message} alt="message" />
                                            </div>
                                            <div className="row">
                                                <div className="col-md-8 col-xl-8 col-sm-12 m-auto">
                                                    <p>Enter the OTP sent to</p>
                                                    <p>
                                                        <span>+91-{userData?.mobileNumber || ''}</span>
                                                        <span className="ml-3">
                                                            <button type="button" className="btn btn-link edit-contact p-0" id="editContact"
                                                                onClick={() => setOtpSent(false)}
                                                            >
                                                                <img src={editIcon} className="mr-1" alt="Edit" />
                                                                Edit
                                                            </button>
                                                        </span>
                                                    </p>
                                                    <div className="form-group otpWrapper">
                                                        <input type='number' pattern="\d*" ref={inputRef[0]} value={enteredOtp[0]} onKeyDown={(e) => handleKeyChange(0, e)} onChange={(e) => handleOtpChange(0, e.target.value)} className="form-control otp-input" id="digit1" maxLength="1" required />
                                                        <input type='number' pattern="\d*" ref={inputRef[1]} value={enteredOtp[1]} onKeyDown={(e) => handleKeyChange(1, e)} onChange={(e) => handleOtpChange(1, e.target.value)} className="form-control otp-input" id="digit2" maxLength="1" required />
                                                        <input type='number' pattern="\d*" ref={inputRef[2]} value={enteredOtp[2]} onKeyDown={(e) => handleKeyChange(2, e)} onChange={(e) => handleOtpChange(2, e.target.value)} className="form-control otp-input" id="digit3" maxLength="1" required />
                                                        <input type='number' pattern="\d*" ref={inputRef[3]} value={enteredOtp[3]} onKeyDown={(e) => handleKeyChange(3, e)} onChange={(e) => handleOtpChange(3, e.target.value)} className="form-control otp-input" id="digit4" maxLength="1" required />
                                                        <input type='number' pattern="\d*" ref={inputRef[4]} value={enteredOtp[4]} onKeyDown={(e) => handleKeyChange(4, e)} onChange={(e) => handleOtpChange(4, e.target.value)} className="form-control otp-input" id="digit5" maxLength="1" required />
                                                        <input type='number' pattern="\d*" ref={inputRef[5]} value={enteredOtp[5]} onKeyDown={(e) => handleKeyChange(5, e)} onChange={(e) => handleOtpChange(5, e.target.value)} className="form-control otp-input" id="digit5" maxLength="1" required />
                                                    </div>
                                                    <div className="d-flex justify-content-between">
                                                        <span id="resendTimer" className="text-muted">I did not receive OTP {(resendTimer !== 0 && startTimer) && <span id="countdown">{getTimerToDisplay()}</span>}</span>
                                                        {
                                                            !(resendTimer !== 0 && startTimer) &&
                                                            <button type="button" onClick={handleResendOtp} className="btn btn-link resend-otp p-0" id="resendOTP">Resend OTP</button>
                                                        }
                                                    </div>
                                                    <div className="otp-submit-cta text-center my-5">
                                                        <button type="submit" className="btn btn-dark" onClick={handleOtpSubmit}>Submit</button>
                                                        {otpError && <p className='errorMessage mt-4' style={{ textAlign: 'center' }}>{otpError}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal>
                )
            }
            {
                showFaceRegistrationModal && (
                    <FaceRegistration
                        showFaceRegistrationModal={showFaceRegistrationModal}
                        setShowFaceRegistrationModal={setShowFaceRegistrationModal}
                        handleDataUpdate={handleDataUpdate}
                        userData={userData}
                    />
                )
            }
        </Modal>
    )
}

export default SignupPage;